import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Badge, Modal, Form, Row, Col } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { Get, Post, DeleteSingle } from '../../utils/httpProvider';
import { ANNUAL_JOB_REGISTRATION_API } from '../../utils/apiUrl';
import { toast } from 'react-toastify';
import Pagination from 'react-js-pagination';
import moment from 'moment';
import DatePicker from '@app/components/CustomDatePicker';
import { useSelector, useDispatch } from 'react-redux';
import { resetSearch } from '@store/reducers/search';

const AnnualJobRegistrationList = () => {
    const history = useHistory();
    const [registrations, setRegistrations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState({ pageIndex: 1, pageSize: 10, keyword: '', fromDate: '', toDate: '' });
    const [totalRecords, setTotalRecords] = useState(0);

    const dispatch = useDispatch();
    const valueSearch = useSelector((state) => state.search.valueSearch);

    useEffect(() => {
        return () => { dispatch(resetSearch()); };
    }, []);

    useEffect(() => {
        if (valueSearch != null && valueSearch !== filter.keyword) {
            setFilter(prev => ({ ...prev, keyword: valueSearch, pageIndex: 1 }));
        }
    }, [valueSearch]);

    useEffect(() => {
        fetchRegistrations();
    }, [filter.pageIndex, filter.pageSize, filter.keyword, filter.fromDate, filter.toDate]);

    const fetchRegistrations = async () => {
        setLoading(true);
        try {
            const res = await Get(ANNUAL_JOB_REGISTRATION_API.GET_MY_REGISTRATIONS, null, filter);
            if (res) {
                const rawData = res.Data ?? res.data ?? res;
                const rawList = Array.isArray(rawData) ? rawData : (rawData.$values || []);
                const normalized = rawList.map(reg => {
                    const rawDetails = reg.Details ?? reg.details;
                    const details = (rawDetails && rawDetails.$values ? rawDetails.$values : rawDetails) || [];
                    return {
                        id: reg.Id ?? reg.id,
                        name: reg.Name ?? reg.name,
                        startDate: reg.StartDate ?? reg.startDate,
                        endDate: reg.EndDate ?? reg.endDate,
                        createdDate: reg.CreatedDate ?? reg.createdDate,
                        status: reg.Status ?? reg.status ?? 0,
                        rejectReason: reg.RejectReason ?? reg.rejectReason,
                        details: details.map(d => ({
                            id: d.Id ?? d.id,
                            registeredScore: d.RegisteredScore ?? d.registeredScore ?? 0
                        }))
                    };
                });
                setRegistrations(normalized);
                setTotalRecords(res.TotalRecords ?? res.totalRecords ?? 0);
            }
        } catch (error) {
            console.error('Error fetching registrations:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Bạn có chắc chắn muốn xóa phiếu đăng ký này?')) {
            try {
                await DeleteSingle(`${ANNUAL_JOB_REGISTRATION_API.DELETE}/${id}`);
                toast.success('Xóa thành công');
                fetchRegistrations();
            } catch (error) {
                console.error('Error deleting:', error);
                toast.error(typeof error === 'string' ? error : (error.message || 'Có lỗi xảy ra'));
            }
        }
    };

    const handleSubmit = async (id) => {
        if (window.confirm('Bạn muốn gửi phiếu này cho cấp trên duyệt?')) {
            try {
                await Post(`${ANNUAL_JOB_REGISTRATION_API.SUBMIT}/${id}`);
                toast.success('Gửi duyệt thành công');
                fetchRegistrations();
            } catch (error) {
                console.error('Error submitting:', error);
                toast.error(typeof error === 'string' ? error : (error.message || 'Có lỗi xảy ra'));
            }
        }
    };

    const handleRecall = async (id) => {
        if (window.confirm('Bạn muốn rút lại phiếu đang chờ duyệt?')) {
            try {
                await Post(`${ANNUAL_JOB_REGISTRATION_API.RECALL}/${id}`);
                toast.success('Đã rút phiếu thành công');
                fetchRegistrations();
            } catch (error) {
                console.error('Error recalling:', error);
                toast.error('Có lỗi xảy ra');
            }
        }
    };

    const renderStatus = (status) => {
        switch (status) {
            case 0: return <Badge variant="secondary">Bản nháp</Badge>;
            case 1: return <Badge variant="warning" text="dark">Chờ duyệt</Badge>;
            case 2: return <Badge variant="success">Đã duyệt</Badge>;
            case 3: return <Badge variant="danger">Từ chối</Badge>;
            default: return <Badge variant="secondary">Khác</Badge>;
        }
    };

    return (
        <div className="container-fluid">
            <Card>
                <Card.Header>
                    <h3 className="card-title">Danh sách phiếu đăng ký thường niên của tôi</h3>
                </Card.Header>
                <Card.Body style={{ minHeight: '600px', paddingBottom: '150px' }}>
                    <div className="d-flex justify-content-between mb-3">
                        <div className="d-flex align-items-center">
                            <label className="mr-2 mb-0">Hiển thị:</label>
                            <Form.Control
                                as="select"
                                size="sm"
                                style={{ width: '80px' }}
                                value={filter.pageSize}
                                onChange={(e) => {
                                    setFilter({ ...filter, pageSize: Number(e.target.value), pageIndex: 1 });
                                }}
                            >
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                            </Form.Control>

                            <Form.Control
                                type="text"
                                size="sm"
                                placeholder="Nhập tên phiếu để tìm kiếm..."
                                style={{ width: '250px', marginLeft: '15px' }}
                                value={filter.keyword || ''}
                                onChange={(e) => setFilter({ ...filter, keyword: e.target.value, pageIndex: 1 })}
                            />
                            <label className="ml-4 mr-2 mb-0">Từ ngày:</label>
                            <div style={{ width: '130px' }}>
                                <DatePicker 
                                    className="form-control form-control-sm w-100"
                                    selected={filter.fromDate ? new Date(filter.fromDate) : null}
                                    onChange={(date) => setFilter({ ...filter, fromDate: date ? moment(date).format('YYYY-MM-DD') : '', pageIndex: 1 })}
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="dd/MM/yyyy"
                                    isClearable
                                />
                            </div>
                            <label className="ml-4 mr-2 mb-0">Đến ngày:</label>
                            <div style={{ width: '130px' }}>
                                <DatePicker 
                                    className="form-control form-control-sm w-100"
                                    selected={filter.toDate ? new Date(filter.toDate) : null}
                                    onChange={(date) => setFilter({ ...filter, toDate: date ? moment(date).format('YYYY-MM-DD') : '', pageIndex: 1 })}
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="dd/MM/yyyy"
                                    isClearable
                                />
                            </div>
                        </div>
                        <Button variant="info" onClick={() => history.push('/annual-job/registration/new')}>
                            <i className="fas fa-plus"></i> Đăng ký mới
                        </Button>
                    </div>

                <div className="table-responsive" style={{ overflow: 'visible' }}>
                    <Table striped bordered hover style={{ fontSize: '15px', tableLayout: 'fixed' }}>
                        <colgroup>
                            <col style={{ width: '5%' }} />
                            <col style={{ width: '30%' }} />
                            <col style={{ width: '12%' }} />
                            <col style={{ width: '12%' }} />
                            <col style={{ width: '14%' }} />
                            <col style={{ width: '12%' }} />
                            <col style={{ width: '15%' }} />
                        </colgroup>
                        <thead className="bg-light">
                            <tr className="text-center">
                                <th style={{ fontSize: '16px' }}>STT</th>
                                <th style={{ fontSize: '16px' }}>Tên phiếu</th>
                                <th style={{ fontSize: '16px' }}>Từ ngày</th>
                                <th style={{ fontSize: '16px' }}>Đến ngày</th>
                                <th style={{ fontSize: '16px' }}>Ngày tạo</th>
                                <th style={{ fontSize: '16px' }}>Trạng thái</th>
                                <th style={{ fontSize: '16px' }}>Thao tác</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="7" className="text-center py-4">Đang tải...</td></tr>
                            ) : registrations.length === 0 ? (
                                <tr><td colSpan="7" className="text-center py-4">Chưa có phiếu đăng ký nào</td></tr>
                            ) : (
                                registrations.map((reg, index) => (
                                    <tr key={reg.id}>
                                        <td className="text-center">{(filter.pageIndex - 1) * filter.pageSize + index + 1}</td>
                                        <td className="font-weight-bold">
                                            <div>
                                                {reg.name}
                                                <span className="text-success ml-2 font-weight-bold" style={{ color: '#28a745', marginLeft: '8px' }}>
                                                    ({(reg.details || []).reduce((sum, d) => sum + (d.registeredScore || d.RegisteredScore || 0), 0).toFixed(2)} điểm)
                                                </span>
                                            </div>
                                            {(reg.status === 3 || reg.Status === 3) && (reg.rejectReason || reg.RejectReason) && (
                                                <div className="text-danger mt-1" style={{ fontSize: '12px', fontWeight: 'normal' }}>
                                                    (Lý do từ chối: {reg.rejectReason || reg.RejectReason})
                                                </div>
                                            )}
                                        </td>
                                        <td className="text-center">{moment(reg.startDate).format('DD/MM/YYYY')}</td>
                                        <td className="text-center">
                                            <div>{moment(reg.endDate).format('DD/MM/YYYY')}</div>
                                            {reg.status === 2 && moment(reg.endDate).isBefore(moment(), 'day') && (
                                                <Badge variant="danger" className="mt-1" style={{ fontSize: '11px' }}>Quá hạn</Badge>
                                            )}
                                        </td>
                                        <td className="text-center">{moment(reg.createdDate).format('DD/MM/YYYY HH:mm')}</td>
                                        <td className="text-center">{renderStatus(reg.status)}</td>
                                        <td className="text-center">
                                            {reg.status === 0 || reg.status === 3 ? (
                                                <>
                                                    <Button variant="link" className="text-success p-0 mr-3" title="Gửi duyệt" onClick={() => handleSubmit(reg.id)}>
                                                        <i className="fas fa-paper-plane fs-5"></i>
                                                    </Button>
                                                    <Button variant="link" className="text-primary p-0 mr-3" title="Sửa" onClick={() => history.push(`/annual-job/registration/${reg.id}`)}>
                                                        <i className="fas fa-edit fs-5"></i>
                                                    </Button>
                                                    <Button variant="link" className="text-danger p-0" title="Xóa" onClick={() => handleDelete(reg.id)}>
                                                        <i className="fas fa-trash fs-5"></i>
                                                    </Button>
                                                </>
                                            ) : reg.status === 1 ? (
                                                <Button variant="link" className="text-warning p-0" title="Rút lại" onClick={() => handleRecall(reg.id)}>
                                                    <i className="fas fa-undo fs-5"></i>
                                                </Button>
                                            ) : (
                                                <Button variant="link" className="text-info p-0" title="Xem chi tiết" onClick={() => history.push(`/annual-job/registration/${reg.id}`)}>
                                                    <i className="fas fa-eye fs-5"></i>
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </div>

                {totalRecords > filter.pageSize && (
                    <div className="d-flex justify-content-end mt-3">
                        <Pagination
                            activePage={filter.pageIndex}
                            itemsCountPerPage={filter.pageSize}
                            totalItemsCount={totalRecords}
                            pageRangeDisplayed={5}
                            onChange={(page) => setFilter({ ...filter, pageIndex: page })}
                            itemClass="page-item"
                            linkClass="page-link"
                        />
                    </div>
                )}
            </Card.Body>
        </Card>
        </div>
    );
};

export default AnnualJobRegistrationList;
